import React, { useEffect, useRef, useSyncExternalStore } from 'react';
import { clsx } from 'clsx';
import BackToGameToastWrapper from './BackToGameToastWrapper';
import { backToGameToastService, BACK_TO_GAME_TOAST_ID } from '../../services/BackToGameToastService';

import styles from './BackToGameToastHost.module.css';

/**
 * Renders the "Continue Playing" toast that BackToGameToastService shows and dismisses: a live
 * region that is always present (so screen readers announce the toast when it is added), the
 * bottom-right container, and the toast's bounce in and out.
 */
const BackToGameToastHost: React.FC = () => {
    const toast = useSyncExternalStore(backToGameToastService.subscribe, backToGameToastService.getToast);
    const toastRef = useRef<HTMLDivElement>(null);
    const hasToast = toast !== null;

    // Alt+T moves focus to the toast, as the live region's label announces.
    useEffect(() => {
        if (!hasToast) return undefined;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.altKey && e.code === 'KeyT') toastRef.current?.focus();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [hasToast]);

    const handleAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
        // Animations inside the toast bubble up here too; only its own enter/exit counts.
        if (e.target === e.currentTarget) backToGameToastService.handleAnimationEnd();
    };

    return (
        <section aria-live="polite" aria-atomic="false" aria-relevant="additions text" aria-label="Notifications Alt+T">
            {toast && (
                <div className={styles.container} tabIndex={-1}>
                    <div
                        ref={toastRef}
                        id={BACK_TO_GAME_TOAST_ID}
                        role="alert"
                        tabIndex={0}
                        className={clsx(styles.toast, toast.phase === 'enter' && styles.enter, toast.phase === 'exit' && styles.exit)}
                        onAnimationEnd={handleAnimationEnd}
                    >
                        <BackToGameToastWrapper
                            gameId={toast.game.gameKey}
                            gameName={toast.game.gameName}
                            gameThumbnail={toast.game.gameThumbnail}
                            gameData={toast.game.gameData}
                        />
                    </div>
                </div>
            )}
        </section>
    );
};

export default BackToGameToastHost;
